import React, { useState } from 'react';
import './StupidSystemDesigns.css';

const pasteBinCode = String.raw`#include <bits/stdc++.h>
using namespace std;

// --- ENTITIES ---

class User {
    int userId;
    string username;
    unordered_set<string> linkIds;
public:
    User(int uId, string uName) {
        userId = uId;
        username = uName;
        linkIds = {};
    }    
    void addLinkId(string linkId) {linkIds.insert(linkId);}
    vector<string> getLinkIds() {
        vector<string> linkArr;
        for(auto &id : linkIds) {
            linkArr.push_back(id);
        }
        return linkArr;
    }
    void removeLinkId(string linkId) {linkIds.erase(linkId);}
    string getUsername() { return username; }
};

class Link {
    string linkId;
    string data;
    int userId;
    string password;
    bool isPrivate;
    int TTL;
public:
    Link(string lId, string d, int uId, bool pvt, string pw, int expiry) 
        : linkId(lId), data(d), userId(uId), isPrivate(pvt), password(pw), TTL(expiry) {};
    
    int getUserId() { return userId; }
    int getTTL() { return TTL; }
    string getPassword() { return password; }
    string getData() { return data; }
    void updateData(string dataNew) { data = dataNew; }
    bool getIsPrivate() { return isPrivate; }
};

// --- SERVICE/CONTROLLER ---

class PasteBin {
    unordered_map<string, Link*> links;
    unordered_map<int, User*> users;
    
    long long linkIndexCounter; 
    int userIdCounter;
    int tick;
    
public:
    PasteBin() {
        linkIndexCounter = 56800235584LL; // Starting at 62^6 for linkid length convenience, thats it.
        userIdCounter = 1;
        tick = 1;
    }
    
    // Simulates time passing
    void advanceTick(int ticks = 1) {
        tick += ticks;
        cout << "\n[TIME] Advanced to Tick " << tick << "\n";
    }

    int addUser(string username) {
        int userId = userIdCounter++;
        User *user = new User(userId, username);
        users[userId] = user;
        return userId;
    }
    
    string addLink(string data, int userId, bool isPrivate, string pw, int ttlDuration) {
        if(!users.count(userId)) {
            cout << "[ERROR] Link creation failed - Invalid User\n";
            return "";
            
        }
        
        User *user = users[userId];
        string linkId = generateLinkId(linkIndexCounter++);
        
        // TTL is Current Tick + Duration
        int expiryTick = tick + ttlDuration; 
        
        Link *link = new Link(linkId, data, userId, isPrivate, pw, expiryTick);
        user->addLinkId(linkId);
        links[linkId] = link;
        
        cout << "[CREATED] Link: " << linkId << " by User: " << user->getUsername() << "\n";
        return linkId;
    }
    
    void getData(string linkId, int requestUserId = -1, string pw = "") {
        if (!links.count(linkId)) {
            cout << "[ERROR] Link " << linkId << " not found or deleted.\n";
            return;
        }
        
        Link *link = links[linkId];
        
        if (link->getTTL() <= tick) {
            cout << "[EXPIRED] Link " << linkId << " has expired. Deleting...\n";
            deleteLink(linkId);
            return;
        }
        
        if (link->getIsPrivate()) {
            if (link->getUserId() != requestUserId && link->getPassword() != pw) {
                cout << "[DENIED] Incorrect password for private link " << linkId << ".\n";
                return;
            }
        }
        
        cout << "[SUCCESS] Data for " << linkId << ": " << link->getData() << "\n";
    }
    
    void TTLCronJob() {
        cout << "[CRON] Running Cleanup Job at tick " << tick << "...\n";
        
        for (auto it = links.begin(); it != links.end(); ) {
            Link* link = it->second;
            
            if (link->getTTL() <= tick) {
                // Remove from user history if actually expired
                User *user = users[link->getUserId()];
                user->removeLinkId(it->first);
                
                delete link;
                it = links.erase(it); 
            } else {
                ++it;
            }
        }
    }
    
private:
    //lazy expiration
    void deleteLink(string linkId) {
        Link* link = links[linkId];
        users[link->getUserId()]->removeLinkId(linkId);
        delete link;
        links.erase(linkId);
    }

    string generateLinkId(long long num) {
        string alphabet = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
        string linkId = "";
        while(num > 0) {
            linkId += alphabet[num % 62];
            num /= 62;
        }
        reverse(linkId.begin(), linkId.end());
        return linkId;
    }
};

// --- DRIVER CODE ---

int main() {
    PasteBin system;
    
    // 1. Setup  - of course, alice and bob are back!
    int u1 = system.addUser("Alice");
    int u2 = system.addUser("Bob");
    
    // 2. Create Pastes
    // format: data, userId, isPrivate, password, TTl_Duration
    string publicLink = system.addLink("Hello World! This is public.", u1, false, "", 10);
    string privateLink = system.addLink("Secret API Keys", u1, true, "supersecret", 5);
    
    // 3. Test Reading Public Link (Success)
    system.getData(publicLink);
    
    // 4. Test Reading Private Link 
    system.getData(privateLink, u2, "wrongpass"); // Bob tries with wrong pass (Deny)
    system.getData(privateLink, u2, "supersecret"); // Bob tries with right pass (Success)
    system.getData(privateLink, u1, ""); // Alice (Owner) tries without pass (Success)
    
    // 5. Simulate Time Passing (Expiration)
    system.advanceTick(6); // Time is now tick 7
    
    // Private link had TTL duration of 5. It should be expired when we try to fetch it.
    // This tests your "Harsh Window / Lazy Read" expiration.
    system.getData(privateLink); 
    
    // 6. Test Cron Job
    // The public link has a duration of 10. Let's advance time to 12.
    system.advanceTick(5);
    system.TTLCronJob(); // Cron job should catch and delete the public link
    
    // 7. Verify deletion
    system.getData(publicLink); // Should print not found

    return 0;
}
`;
const stupidPasteBinCode = String.raw`#include <bits/stdc++.h>
using namespace std;

// --- UTILS ---
string removeVowels(string str) {
    string vowels = "aeiouAEIOU";
    string result = "";
    for (char c : str) {
        if (vowels.find(c) == string::npos) {
            result += c;
        }
    }
    return result;
}

// --- ENTITIES ---
class Link {
    string linkId;
    string data;
    int userId;
    bool isPrivate;
    int captchaAnswer;
public:
    Link(string lId, string d, int uId, bool pvt) 
        : linkId(lId), userId(uId), isPrivate(pvt) {
        
        // ENTERPRISE COMPRESSION: Delete all vowels to save database space
        data = removeVowels(d);
        
        // MILITARY SECURITY: Generate a random math problem for private links
        if (isPrivate) {
            int a = rand() % 50;
            int b = rand() % 50;
            captchaAnswer = a + b;
            cout << "[SECURITY ALERT] To access this private link later, you must know that " 
                 << a << " + " << b << " = " << captchaAnswer << "\n";
        }
    }
    
    string getData() { return data; }
    bool getIsPrivate() { return isPrivate; }
    int getCaptcha() { return captchaAnswer; }
};

// --- CHAOS CONTROLLER ---
class StupidPasteBin {
    unordered_map<string, Link*> links;
    int tick = 0;
    
public:
    StupidPasteBin() {
        srand(time(0));
    }

    string addLink(string data, int userId, bool isPrivate) {
        // SEO OPTIMIZATION: The link ID is just the entire string reversed.
        string linkId = data;
        reverse(linkId.begin(), linkId.end());
        
        // Replace spaces with underscores so it looks like a "valid" URL
        replace(linkId.begin(), linkId.end(), ' ', '_');
        
        Link *link = new Link(linkId, data, userId, isPrivate);
        links[linkId] = link;
        
        cout << "[CREATED] Success! Your link ID is: " << linkId << "\n";
        return linkId;
    }
    
    void getData(string linkId, int captchaAttempt = -1) {
        if (!links.count(linkId)) {
            cout << "[ERROR] Link not found. It either never existed or the Cron Job ate it.\n";
            return;
        }
        
        // SCHRÖDINGER'S TTL: 30% chance to just delete the paste upon being read
        if (rand() % 100 < 30) {
            cout << "[SCHRODINGER TRIGGERED] The database got nervous someone was looking at it. Link " 
                 << linkId << " has self-destructed before you could read it.\n";
            delete links[linkId];
            links.erase(linkId);
            return;
        }
        
        Link *link = links[linkId];
        
        // Security Check
        if (link->getIsPrivate()) {
            if (captchaAttempt != link->getCaptcha()) {
                cout << "[DENIED] Math is incorrect. Hacker detected. HACKERRRRRRR\n";
                return;
            }
        }
        
        cout << "[SUCCESS] Paste Data (De-voweled): " << link->getData() << "\n";
    }
    
    void claustrophobicCronJob() {
        cout << "\n[CRON] Running Garbage Collection...\n";
        if (links.size() >= 3) {
            // Pick a completely random link to destroy
            auto it = links.begin();
            advance(it, rand() % links.size());
            
            cout << "[CRON ANGER] Too many pastes! Database feels crowded. Randomly deleting link: " << it->first << "\n";
            delete it->second;
            links.erase(it);
        } else {
            cout << "[CRON] Database has enough breathing room. All is well.\n";
        }
    }
};

// --- DRIVER CODE ---
int main() {
    StupidPasteBin system;
    
    cout << "--- WELCOME TO CHAOSBIN ---\n\n";

    // 1. Create a public paste (Notice the absurd Link ID and missing vowels)
    cout << "--- ALICE POSTS A PUBLIC MESSAGE ---\n";
    string pubLink = system.addLink("Hello world this is a normal string", 1, false);
    
    // 2. Read the public paste
    cout << "\n--- READING PUBLIC PASTE ---\n";
    system.getData(pubLink);

    // 3. Create a private paste (Notice the Math Captcha)
    cout << "\n--- BOB POSTS A PRIVATE MESSAGE ---\n";
    string pvtLink = system.addLink("Super secret launch codes for the server", 2, true);

    // 4. Try to read private paste with bad math
    cout << "\n--- READING PRIVATE PASTE (WRONG MATH) ---\n";
    system.getData(pvtLink, 999);

    // 5. Trigger the Claustrophobic Cron Job
    cout << "\n--- FILLING THE DATABASE ---\n";
    system.addLink("Another random message just to fill space", 3, false);
    system.claustrophobicCronJob(); // Should delete a random paste because there are exactly 3

    
    cout << "\n--- TESTING SCHRODINGER'S TTL ---\n";
    cout << "Attempting to read the same link 10 times...\n";
    
    string doomedLink = system.addLink("I sure hope nothing bad happens to me", 4, false);
    // This shold delete the link after first call
    for(int i = 1; i <= 3; i++) {
        cout << "Read Attempt " << i << ": ";
        system.getData(doomedLink);
    }

    return 0;
}`;

function PasteBinDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState({ good: false, stupid: false });

    const handleCopyCode = async (codeKey, codeText) => {
        try {
            await navigator.clipboard.writeText(codeText);
            setCopied((previous) => ({ ...previous, [codeKey]: true }));
            window.setTimeout(() => {
                setCopied((previous) => ({ ...previous, [codeKey]: false }));
            }, 1800);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>PasteBin</h1>
                            <p className="system-designs-subtitle">Ever heard of pastebin? Its a cool website, i would recommend you check it out!</p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li>Users can add data/text/thoughts and create links</li>
                                <li>Each link id will be unique, and either public/private</li>
                                <li>Public links data accessible to anyone</li>
                                <li>Private links are only for user, password protected for others (ex: link sharing)</li>
                                <li>Links can have TTL/expiry</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Design</h2>
                            <ul>
                                <li>Fairly simple design, an sql database stores link id, link's data and other fields, probably cache hot links. App servers for logic, gateway for rate limiting, server routing so on.</li>
                                <li>Make sure links are unique - say base 62 encoding</li>
                                <li>if it was scaled, i would probably pre-generate the links using a KGS(key generation service) and pre-store around 1k-10k new links in either app servers or cache. Other options will be doing md5 hashing on data itself (still can cause collisions) and snowflake link id generation.</li>
                                <li>For TTL of links, one way is to run a cron job on DB once a day. Then what happens for the harsh spot of time when ttl is expired for link, but db hasnt purged yet? In service layer (both cache and app servers) befor returning data, just check the ttl time with current time  :p</li>
                            </ul>
                        <br></br>
                            Anyways.. for now we focus on the low level design folks!
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>Link id</li>
                                <li>Link data</li>
                                <li>Public/private visibility</li>
                                <li>Password</li>
                                <li>TTL</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <ol>
                                <li><strong>Enterprise Compression Algorithm:</strong> To save on AWS database costs, the system permanently deletes all vowels from any text you paste.</li>
                                <li><strong>SEO-Optimized Link IDs:</strong> Base62 is too short and doesn't help with Google rankings. Your Link ID is now just your entire paste's text, completely reversed. If you paste a 5,000-word essay, your URL is a 5,000-word reversed string.</li>
                                <li><strong>Schrodinger's Expiration (TTL):</strong> Time-based expiration is too predictable. Instead, every time someone tries to read a paste, there is a 30% chance the system gets nervous and permanently deletes the link before showing it to them.</li>
                                <li><strong>Military-Grade Security:</strong> Text passwords are out. To access a "private" paste, the user must successfully answer a randomly generated math problem to prove they aren't a robot.</li>
                                <li><strong>Aggressive Garbage Collection:</strong> If the database ever exceeds 3 pastes, the Cron Job gets claustrophobic and permanently deletes a random paste.</li>
                            </ol>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Stupid C++ Sample Code</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('stupid', stupidPasteBinCode)}>
                                    {copied.stupid ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{stupidPasteBinCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>C++ Working logic Code</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('good', pasteBinCode)}>
                                    {copied.good ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{pasteBinCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        *Adds a footer note. *Realises its pointless and no-one visits his website anyways. *Deletes footer note
                    </div>

                    <button className="back-btn" onClick={onBackClick}>
                        Back to Designs
                    </button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default PasteBinDesign;